import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Copy, ExternalLink, RefreshCw, MapPin, Calendar, Clock, Package, CircleCheck as CheckCircle, Truck, TriangleAlert as AlertTriangle, Loader as Loader2, ShoppingBag, User, Globe, Info } from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';
import {
  fetchSingleTrackingStatus,
  getPostNLTrackingUrl,
  type TrackedPackage,
  type TrackingEvent,
} from '../../lib/trackingService';
import { formatTrackingStatusLabel, getStatusBadgeClasses } from '../../utils/trackingStatusHelpers';

interface PackageDetailPanelProps {
  pkg: TrackedPackage;
  adminId: string;
  onClose: () => void;
  onStatusUpdated: (updatedPkg: Partial<TrackedPackage> & { id: string }) => void;
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'delivered':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'in_transit':
    case 'out_for_delivery':
      return <Truck className="h-4 w-4 text-blue-500" />;
    case 'failed_delivery':
    case 'returned':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'no_info':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'unconfirmed':
      return <Clock className="h-4 w-4 text-gray-400" />;
    default:
      return <Clock className="h-4 w-4 text-amber-500" />;
  }
}

export const PackageDetailPanel: React.FC<PackageDetailPanelProps> = ({
  pkg,
  adminId,
  onClose,
  onStatusUpdated,
}) => {
  const { success: notifySuccess, error: notifyError } = useNotification();
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [rawStatus, setRawStatus] = useState<{ code?: string; phase?: string; description?: string } | null>(null);

  const onStatusUpdatedRef = useRef(onStatusUpdated);
  onStatusUpdatedRef.current = onStatusUpdated;

  const trackUrl = getPostNLTrackingUrl(pkg.tracking_code, pkg.shipping_address);
  const address = pkg.shipping_address || {};
  const lineItems = Array.isArray(pkg.line_items) ? pkg.line_items : [];

  const fetchAndUpdate = useCallback(async (showNotification: boolean) => {
    const result = await fetchSingleTrackingStatus(pkg.tracking_code, adminId);
    if (result.events?.length > 0) {
      setEvents(result.events);
    }
    if (result.rawStatusCode !== undefined || result.rawPhaseCode !== undefined || result.rawStatusDescription !== undefined) {
      setRawStatus({
        code: result.rawStatusCode,
        phase: result.rawPhaseCode,
        description: result.rawStatusDescription,
      });
    }
    if (result.deliveryStatus) {
      onStatusUpdatedRef.current({
        id: pkg.id,
        delivery_status: result.deliveryStatus,
        estimated_delivery: result.estimatedDelivery,
        last_tracking_update: new Date().toISOString(),
      });
    }
    if (showNotification) {
      notifySuccess('Tracking status updated');
    }
  }, [pkg.tracking_code, pkg.id, adminId, notifySuccess]);

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await fetchAndUpdate(true);
    } catch {
      notifyError('Failed to fetch tracking status');
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, fetchAndUpdate, notifyError]);

  const shouldAutoFetch = !pkg.last_tracking_update ||
    !['delivered', 'returned'].includes(pkg.delivery_status);

  useEffect(() => {
    if (!shouldAutoFetch) return;
    let cancelled = false;
    setLoadingEvents(true);
    fetchAndUpdate(false)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingEvents(false);
      });

    return () => { cancelled = true; };
  }, [fetchAndUpdate, shouldAutoFetch]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(pkg.tracking_code);
    notifySuccess('Tracking code copied');
  }, [pkg.tracking_code, notifySuccess]);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over Panel */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white dark:bg-gray-900 z-50 shadow-2xl overflow-y-auto transform transition-transform duration-300 animate-slide-in-right">
        {/* Panel Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                  {pkg.order_number}
                </h2>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(pkg.delivery_status)}`}>
                  {formatTrackingStatusLabel(pkg.delivery_status)}
                </span>
                {pkg.is_estimated && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                    Est.
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {pkg.customer_name || 'Unknown Customer'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Tracking Info Section */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Tracking Information
            </h3>
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Tracking Code</p>
                  <p className="font-mono text-base font-semibold text-gray-900 dark:text-gray-100">
                    {pkg.tracking_code}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleCopy}
                    className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500"
                    title="Copy"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                  PostNL
                </span>
                {pkg.estimated_delivery && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                    <Calendar className="h-3 w-3" />
                    Est. {new Date(pkg.estimated_delivery).toLocaleDateString()}
                  </span>
                )}
              </div>

              <a
                href={trackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 w-full justify-center px-4 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-500/25 transition-all"
              >
                <ExternalLink className="h-4 w-4" />
                Track on PostNL.nl
              </a>

              {rawStatus && (rawStatus.code || rawStatus.phase || rawStatus.description) && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                    Raw PostNL Response
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {rawStatus.code && (
                      <div className="bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                        <p className="text-[10px] text-gray-400 mb-0.5">StatusCode</p>
                        <p className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-200">{rawStatus.code}</p>
                      </div>
                    )}
                    {rawStatus.phase && (
                      <div className="bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                        <p className="text-[10px] text-gray-400 mb-0.5">PhaseCode</p>
                        <p className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-200">{rawStatus.phase}</p>
                      </div>
                    )}
                  </div>
                  {rawStatus.description && (
                    <div className="mt-2 bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                      <p className="text-[10px] text-gray-400 mb-0.5">StatusDescription</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">{rawStatus.description}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Recipient Section */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Recipient
            </h3>
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 space-y-2">
              {(address.name || pkg.customer_name) && (
                <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                  <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  {address.name || pkg.customer_name}
                </div>
              )}
              {(address.address1 || address.street) && (
                <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p>{address.address1 || address.street}</p>
                    {address.address2 && <p>{address.address2}</p>}
                    <p>
                      {[address.zip || address.postal_code, address.city].filter(Boolean).join(' ')}
                    </p>
                  </div>
                </div>
              )}
              {(address.country || address.country_code) && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Globe className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  {address.country || address.country_code}
                </div>
              )}
            </div>
          </section>

          {/* Tracking Timeline */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Tracking Timeline
              </h3>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadingEvents && events.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-teal-500" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                  Fetching live tracking data...
                </span>
              </div>
            ) : events.length > 0 ? (
              <div className="relative pl-6 space-y-0">
                <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-teal-500 via-teal-300 to-gray-200 dark:from-teal-400 dark:via-teal-600 dark:to-gray-700" />
                {events.map((event, i) => (
                  <div key={i} className="relative pb-5 last:pb-0">
                    <div
                      className={`absolute -left-6 top-1 w-[22px] h-[22px] rounded-full flex items-center justify-center ${
                        i === 0
                          ? 'bg-teal-500 text-white ring-4 ring-teal-100 dark:ring-teal-900/30'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {i === 0 ? (
                        getStatusIcon(pkg.delivery_status)
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500" />
                      )}
                    </div>
                    <div className={`ml-4 ${i === 0 ? '' : 'opacity-80'}`}>
                      <p className={`text-sm font-medium ${
                        i === 0 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {event.description || formatTrackingStatusLabel(event.status)}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        {event.location && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                          </span>
                        )}
                        {event.timestamp && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="h-3 w-3" />
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 text-center">
                <Info className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {pkg.is_estimated
                    ? 'Status is estimated based on shipping date. Click Refresh to fetch live tracking data from PostNL.'
                    : 'No tracking events yet. Click Refresh to fetch live data from PostNL.'}
                </p>
              </div>
            )}
          </section>

          {/* Order Summary */}
          {lineItems.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Order Summary
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 space-y-3">
                {lineItems.map((item: any, i: number) => (
                  <div key={i} className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <ShoppingBag className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {item.title || item.name || 'Item'}
                        </p>
                        {item.variant_title && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.variant_title}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        x{item.quantity || 1}
                      </p>
                      {item.price && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {pkg.currency === 'EUR' ? '\u20AC' : pkg.currency}{parseFloat(item.price).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Total</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {pkg.currency === 'EUR' ? '\u20AC' : (pkg.currency || '\u20AC')}
                    {Number(pkg.total_price || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};
